// Reads the client's name off a fee slip: its text, or its file name.
//
// An emailed fee slip was named after its email's subject, which often says nothing about
// the client ("o/s fees - Janey Fisher (nee Nestadt) - Dever's List", "Greens List
// Barristers - Tax Invoice - INV-692736"). The fee slip itself always does (Emma, 26 Oct):
//
//   Barristers' invoices (Meldrum's, Dever's, Holmes, Parnell's, Foley's):
//     text "MATTER: HOOD, Baymos v Informant (Wilson; Hill); Henry"
//     file "HOOD, Baymos v Informant Wilson; Hill; Henry Invoice 00949042 2742026.pdf"
//          "Allen Williams Invoice 01418141 1592026.pdf", "BECK, Robert 2026 Invoice ..."
//   Greens: text "MATTER:" with "Lara McCoy v State of Victoria" on the next line; the file
//     is just "INV-692736.pdf".
//
// The client is the first party of the matter, so "v Informant ...", "v The King", "v State
// of Victoria" and the like are dropped. Pure, so the intake (server) and the fee slip page
// (browser) read names the same way.

const clean = (value) => String(value ?? "").replace(/\s+/g, " ").trim();

// "HOOD, Baymos v Informant (Wilson; Hill); Henry" -> "HOOD, Baymos"
// "Nicole MANSON - SCV Bail" -> "Nicole MANSON"; "Jessica Clark v The King (CDPP)" -> "Jessica Clark"
// When the prosecution is named first ("R v Thomas Towle", "DPP v SMITH, Jo") the client is
// the other party.
const PROSECUTION = /^(?:r|the king|the queen|dpp|cdpp|opp|police|informant|state of victoria)$/i;

export function firstParty(matter = "") {
  const parties = clean(matter).split(/\s+(?:v|vs|v\.|versus)\s+/i).map((part) => part.trim()).filter(Boolean);
  let name = parties.length > 1 && PROSECUTION.test(parties[0]) ? parties[1] : parties[0] || "";
  name = name
    .replace(/\s+-\s+.*$/, "")
    .replace(/\s*\(.*$/, "")
    // What the work was, after the name: "Richard HAGAN Verdins Assessment and Report".
    .replace(/\s+(?:verdins|assessment|report|invoice|fees?|appearance|plea|bail|hearing|mention|sentence)\b.*$/i, "")
    .replace(/[.,;:\s]+$/, "")
    .trim();
  if (PROSECUTION.test(name) || NOT_A_CLIENT.test(name)) return "";
  // A person's name has no digits: "INV-688027" on a RE: line is an invoice number.
  if (/\d/.test(name)) return "";
  return name.length >= 3 && name.length <= 80 && /[a-z]/i.test(name) ? name : "";
}

// Who is billed, not who the work was for: a "Client:" line on a provider's invoice.
const NOT_A_CLIENT = /legal service|aboriginal legal|law (?:and|&) advocacy|\blacw\b|\bvals\b|legal aid|complete law support/i;

// The MATTER: line, with its value on the same line or the next.
export function clientFromInvoiceText(text = "") {
  const lines = String(text || "").split(/\r?\n/).map(clean).filter(Boolean);
  for (let i = 0; i < lines.length; i += 1) {
    const match = lines[i].match(/^(?:matter|re|client(?:\s+name)?)\s*:\s*(.*)$/i);
    if (!match) continue;
    const value = match[1] || lines[i + 1] || "";
    // The next line is a value, not another label.
    if (!match[1] && /^[a-z ]+:\s*$/i.test(value)) continue;
    const name = firstParty(value);
    if (name) return name;
  }
  return "";
}

// "HOOD, Baymos v Informant Wilson; Hill; Henry Invoice 00949042 2742026.pdf" -> "HOOD, Baymos"
// "BECK, Robert 2026 Invoice 00155306 162026.pdf" -> "BECK, Robert"
export function clientFromFileName(fileName = "") {
  const base = clean(String(fileName || "").replace(/(?:\.pdf)+$/i, ""));
  const match = base.match(/^(.+?)\s+(?:20\d\d\s+)?Invoice\s+\d{6,}\b/i);
  return match ? firstParty(match[1]) : "";
}
